import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Calendar } from "lucide-react";
import Navbar from "../components/Navbar";
import Title from "../components/Title";
import SessionCardItem from "../components/SessionCardItem";
import TabBar from "../components/TabBar";
import CalendarStrip, { CalendarDay } from "../components/CalendarStrip";
import { getTrainerBookings } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

type Tab = "upcoming" | "completed";

type UpcomingSessionItem = {
  id: string;
  date: string;              // "AUG 12"
  time: string;              // "10:00 AM - 11:00AM"
  participant: string;       // "Name, age, gender"
  countdown?: string | null; // if present -> green bar shows
  isHighlighted?: boolean;
  status: string;
  rawBooking?: any;
};

type CompletedSessionItem = {
  id: string;
  date: string;
  time: string;
  participant: string;
  earnings: string;
  status: string;
  rawBooking?: any;
};


/* ─────────────────────────────
   Component
────────────────────────────── */
const TrainerSessions: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>("upcoming");
  const [selectedDate, setSelectedDate] = useState("12");
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSessionItem[]>([]);
  const [completedSessions, setCompletedSessions] = useState<CompletedSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch trainer sessions
  useEffect(() => {
    const fetchSessions = async () => {
      if (!user?.id) {
        console.log('TrainerSessions - No user ID, skipping data fetch');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const bookingsData = await getTrainerBookings(user.id);
        console.log('TrainerSessions - Raw bookings data:', bookingsData.bookings);

        const now = new Date();

        // Process upcoming sessions (future sessions with confirmed or pending status)
        const upcoming = bookingsData.bookings
          ?.filter((booking: any) => {
            const startTime = new Date(booking.startDateTime);
            return startTime > now && (booking.status === 'confirmed' || booking.status === 'pending');
          })
          .map((booking: any, index: number) => {
            const startDate = new Date(booking.startDateTime);
            const endDate = new Date(booking.endDateTime);

            // Format date (e.g., "AUG 12")
            const dateStr = startDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            }).toUpperCase();

            // Format time (e.g., "10:00 AM - 11:00 AM")
            const timeStr = `${startDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })} - ${endDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}`;

            // Only show countdown for the first upcoming session
            let countdown = null;
            if (index === 0) {
              const timeUntil = startDate.getTime() - now.getTime();
              const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
              const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));

              if (days > 0) {
                countdown = `${days}d ${hours}h ${minutes}m`;
              } else if (hours > 0) {
                countdown = `${hours}h ${minutes}m`;
              } else {
                countdown = `${minutes}m`;
              }
            }

            return {
              id: booking._id,
              date: dateStr,
              time: timeStr,
              participant: `${booking.userId?.name || 'Unknown Student'}, ${booking.userId?.age || 'Unknown'}, ${booking.userId?.sex || 'Unknown'}`,
              countdown: countdown,
              isHighlighted: index === 0,
              status: booking.status,
              rawBooking: booking
            };
          }) || [];

        // Process completed sessions (past sessions or cancelled sessions)
        const completed = bookingsData.bookings
          ?.filter((booking: any) => {
            const startTime = new Date(booking.startDateTime);
            return startTime <= now || booking.status === 'cancelled';
          })
          .map((booking: any) => {
            const startDate = new Date(booking.startDateTime);
            const endDate = new Date(booking.endDateTime);

            // Format date (e.g., "AUG 12")
            const dateStr = startDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            }).toUpperCase();

            // Format time (e.g., "10:00 AM - 11:00 AM")
            const timeStr = `${startDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })} - ${endDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}`;

            // Calculate earnings (assuming $50 per hour for now)
            const durationInHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
            const earnings = `$${Math.round(durationInHours * 50)}`;

            return {
              id: booking._id,
              date: dateStr,
              time: timeStr,
              participant: `${booking.userId?.name || 'Unknown Student'}, ${booking.userId?.age || 'Unknown'}, ${booking.userId?.sex || 'Unknown'}`,
              earnings: earnings,
              status: booking.status,
              rawBooking: booking
            };
          }) || [];

        setUpcomingSessions(upcoming);
        setCompletedSessions(completed);

        console.log('TrainerSessions - Processed upcoming sessions:', upcoming);
        console.log('TrainerSessions - Processed completed sessions:', completed);

      } catch (err) {
        console.error('Error fetching trainer sessions:', err);
        setError('Failed to load sessions');
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [user]);

  const sessionsData = {
    upcomingSessions,
    completedSessions,
  };

  // Generate calendar dates from actual sessions
  const generateCalendarDates = (): CalendarDay[] => {
    const allSessions = [...upcomingSessions, ...completedSessions];
    const uniqueDates = new Set<string>();

    allSessions.forEach(session => {
      uniqueDates.add(session.date);
    });

    const calendarDates = Array.from(uniqueDates).map((dateStr, index) => {
      const [month, day] = dateStr.split(' ');
      const date = new Date(`${month} ${day}, ${new Date().getFullYear()}`);
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();

      return {
        id: index + 1,
        day,
        month,
        dayName: weekday,
        isSelected: index === 0
      };
    });

    return calendarDates.length > 0 ? calendarDates : [
      { id: 1, day: "12", month: "AUG", dayName: "WED", isSelected: true },
      { id: 2, day: "13", month: "AUG", dayName: "THU", isSelected: false },
      { id: 3, day: "14", month: "AUG", dayName: "FRI", isSelected: false },
      { id: 4, day: "15", month: "AUG", dayName: "SAT", isSelected: false },
    ];
  };

  const calendarDates = generateCalendarDates();

  const handleDaySelect = (dayId: number) => {
    setSelectedDate(calendarDates.find(d => d.id === dayId)?.day || "12");
    // Update calendar selection
    const updatedDates = calendarDates.map(day => ({
      ...day,
      isSelected: day.id === dayId
    }));
    // Note: In a real implementation, you might want to store this in state
    // For now, we'll just update the selectedDate
  };


  return (
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full min-h-full flex flex-col">
        {/* Top nav */}
        <Navbar onBack={() => navigate(-1)} />

        {/* Title */}
        <section className="px-5 pt-2">
          <Title text="// All Sessions" />
        </section>

        {/* Tabs */}
        <TabBar
          tabs={["UPCOMING", "COMPLETED"]}
          active={activeTab === "upcoming" ? 0 : 1}
          onChange={(index) => setActiveTab(index === 0 ? "upcoming" : "completed")}
        />

        {/* Date Picker */}
        <section className="px-5 pt-4">
          <CalendarStrip
            days={calendarDates}
            onDaySelect={handleDaySelect}
            loading={loading}
            emptyMessage="No sessions available"
          />
        </section>

        {/* List */}
        <main className="px-5 pt-4 pb-10">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
                <p className="text-gray-600">Loading sessions...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="text-[#D62422] font-semibold"
                >
                  Try Again
                </button>
                <button
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="text-gray-600 font-semibold"
                >
                  Back to Role Selection
                </button>
              </div>
            </div>
          ) : activeTab === "upcoming" ? (
            <div className="space-y-4">
              {sessionsData.upcomingSessions.length > 0 ? (
                sessionsData.upcomingSessions.map((session) => {
                  const [m, d] = session.date.split(" ");
                  return (
                    <div key={session.id} className="w-full">
                      <SessionCardItem
                        month={m}
                        day={d}
                        time={session.time}
                        participant={session.participant}
                        countdown={session.countdown ?? undefined}
                        layout="column"
                        onClick={() => {
                          const booking = session.rawBooking || {};
                          navigate("/trainer/session-details", {
                            state: {
                              session: {
                                ...booking,
                                startDateTime: booking.startDateTime || '',
                                endDateTime: booking.endDateTime || '',
                                location: booking.location || 'HOME',
                                address: booking.address || 'Address not specified',
                                sessionIn: session.countdown || '',
                                studentName: booking.userId?.name || 'Student',
                                studentAge: booking.userId?.age?.toString() || '',
                                studentGender: booking.userId?.sex || '',
                                studentLevel: booking.userId?.experience || 'BEGINNER',
                                studentCountryCode: booking.userId?.countryCode || '',
                                studentMobileNumber: booking.userId?.mobileNumber || '',
                                notes: booking.notes || '',
                                requirements: {
                                  gloves: booking.userId?.hasGloves ? 'YES' : 'NO',
                                  gloveSize: (() => {
                                    const sizeMap: Record<string, string> = { '8': 'XS', '10': 'S', '12': 'M', '14': 'L' };
                                    const val = booking.userId?.gloveSize?.toString() || '';
                                    return sizeMap[val] || val;
                                  })(),
                                  handWraps: booking.userId?.hasHandwrap ? 'YES' : 'NO',
                                },
                              }
                            }
                          });
                        }}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Calendar size={48} color="#9ca3af" className="mx-auto mb-4" />
                  <p className="text-gray-500 font-semibold mb-2">No upcoming sessions</p>
                  <p className="text-gray-400 text-sm">You don't have any upcoming sessions scheduled.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {sessionsData.completedSessions.length > 0 ? (
                sessionsData.completedSessions.map((session) => {
                  const [m, d] = session.date.split(" ");
                  return (
                    <div key={session.id} className="w-full">
                      <SessionCardItem
                        month={m}
                        day={d}
                        time={session.time}
                        participant={session.participant}
                        layout="column"
                        onClick={() => {
                          const booking = session.rawBooking || {};
                          navigate("/trainer/session-details", {
                            state: {
                              session: {
                                ...booking,
                                startDateTime: booking.startDateTime || '',
                                endDateTime: booking.endDateTime || '',
                                location: booking.location || 'HOME',
                                address: booking.address || 'Address not specified',
                                sessionIn: '',
                                studentName: booking.userId?.name || 'Student',
                                studentAge: booking.userId?.age?.toString() || '',
                                studentGender: booking.userId?.sex || '',
                                studentLevel: booking.userId?.experience || 'BEGINNER',
                                studentCountryCode: booking.userId?.countryCode || '',
                                studentMobileNumber: booking.userId?.mobileNumber || '',
                                notes: booking.notes || '',
                                requirements: {
                                  gloves: booking.userId?.hasGloves ? 'YES' : 'NO',
                                  gloveSize: (() => {
                                    const sizeMap: Record<string, string> = { '8': 'XS', '10': 'S', '12': 'M', '14': 'L' };
                                    const val = booking.userId?.gloveSize?.toString() || '';
                                    return sizeMap[val] || val;
                                  })(),
                                  handWraps: booking.userId?.hasHandwrap ? 'YES' : 'NO',
                                },
                              }
                            }
                          });
                        }}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Calendar size={48} color="#9ca3af" className="mx-auto mb-4" />
                  <p className="text-gray-500 font-semibold mb-2">No completed sessions</p>
                  <p className="text-gray-400 text-sm">You haven't completed any sessions yet.</p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default TrainerSessions;
